import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { checkPassword, PASSWORD_RULE } from "@/lib/password";
import AnnouncementsCard from "@/components/AnnouncementsCard";
import { MyHrDocuments } from "@/components/HrDocsShared";
import { MyPolicies } from "@/components/HrPolicies";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Upload, Trash2, KeyRound, Camera, Clock, LogIn, LogOut, StickyNote, ListTodo, ChevronRight, CalendarDays, RefreshCw } from "lucide-react";
import { PageIcon } from "../components/PageIcon";
import { ExpenseForm } from "@/pages/ExpensesPage";
import { Receipt } from "lucide-react";
import { MyExpenses } from "@/components/MyExpenses";
import { MyContacts } from "@/components/MyContacts";

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const [expOpen, setExpOpen] = useState(false);
  const [expKey, setExpKey] = useState(0);
  const [f, setF] = useState({ first_name: "", surname: "", phone: "", job_title: "", bio: "", weekly_hours: "", requires_checkin: false });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [ts, setTs] = useState(null);
  const [ciBusy, setCiBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (user && user !== true) {
      const parts = (user.name || "").trim().split(/\s+/).filter(Boolean);
      setF({ first_name: parts[0] || "", surname: parts.slice(1).join(" "), phone: user.phone || "", job_title: user.job_title || "", bio: user.bio || "",
             weekly_hours: user.weekly_hours ?? "", requires_checkin: !!user.requires_checkin });
    }
  }, [user]);

  const loadTs = () => api.get("/profile/timesheet").then((r) => setTs(r.data)).catch(() => {});
  useEffect(() => { loadTs(); }, []);

  useEffect(() => {
    let revoked = null;
    const path = user && user !== true ? user.avatar_path : null;
    if (!path) { setAvatarUrl(null); return; }
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data); revoked = url; setAvatarUrl(url);
    }).catch(() => setAvatarUrl(null));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [user]);

  const fullName = [f.first_name, f.surname].filter(Boolean).join(" ").trim();
  const initials = (fullName || user?.email || "?").split(/\s+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await api.put("/profile", {
        name: [f.first_name, f.surname].filter(Boolean).join(" ").trim(),
        phone: f.phone, job_title: f.job_title, bio: f.bio,
        weekly_hours: f.weekly_hours === "" ? null : Number(f.weekly_hours),
        requires_checkin: f.requires_checkin,
      });
      updateUser(r.data.user);
      toast.success("Profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save profile"); }
    finally { setSavingProfile(false); }
  };

  const fmtTime = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
  const toggleCheckinReq = async (v) => {
    set("requires_checkin", v);
    try { await api.put("/profile", { requires_checkin: v }); updateUser({ requires_checkin: v }); }
    catch { toast.error("Could not update"); }
  };
  const doCheck = async (kind) => {
    setCiBusy(true);
    try {
      const r = await api.post(`/profile/${kind}`);
      await loadTs();
      if (kind === "checkin" && r.data?.warning) toast.warning(r.data.warning);
      else toast.success(kind === "checkin" ? "Checked in" : "Checked out");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCiBusy(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateUser({ avatar_path: r.data.avatar_path });
      toast.success("Photo updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const removeAvatar = async () => {
    try { await api.delete("/profile/avatar"); updateUser({ avatar_path: null }); setAvatarUrl(null); toast.success("Photo removed"); }
    catch { toast.error("Could not remove photo"); }
  };

  const changePassword = async () => {
    const err = checkPassword(pw.new_password);
    if (err) { toast.error(err); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/profile/password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed. Please sign in again with your new password.");
      logout();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); }
    finally { setSavingPw(false); }
  };

  const lbl = "text-xs font-semibold uppercase tracking-wide text-slate-500";

  const pwErr = checkPassword(pw.new_password);
  const newCls = !pw.new_password ? "" : (pwErr ? " border-rose-400 focus-visible:ring-rose-200" : " border-emerald-500 focus-visible:ring-emerald-200");
  const confCls = !pw.confirm ? "" : (!pwErr && pw.confirm === pw.new_password ? " border-emerald-500 focus-visible:ring-emerald-200" : " border-rose-400 focus-visible:ring-rose-200");

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="profile-page">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <PageIcon />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
          <p className="text-sm text-slate-500">Manage your details, photo and password.</p>
        </div>
        <button onClick={() => setExpOpen(true)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-medium" data-testid="profile-add-expense-btn">
          <Receipt className="w-4 h-4" /> Add Expense
        </button>
      </div>
      {expOpen && <ExpenseForm onClose={() => setExpOpen(false)} onSaved={() => setExpKey((k) => k + 1)} />}

      {/* Combined profile card: photo + details + password in one */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6 mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Photo */}
          <div className="flex flex-col items-center text-center lg:border-r lg:border-slate-100 lg:pr-6">
            <div className="relative">
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" className="w-28 h-28 rounded-full object-cover border border-slate-200" data-testid="profile-avatar-img" />
                : <div className="w-28 h-28 rounded-full bg-indigo-600 text-white text-3xl font-bold flex items-center justify-center" data-testid="profile-avatar-initials">{initials}</div>}
              <button onClick={() => fileRef.current?.click()} className="absolute bottom-0 right-0 bg-white border border-slate-200 rounded-full p-1.5 shadow hover:bg-slate-50" title="Change photo"><Camera className="w-4 h-4 text-slate-600" /></button>
            </div>
            <div className="mt-3 font-semibold text-slate-900">{fullName || user?.name}</div>
            <div className="text-xs text-slate-500">{user?.email}</div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="profile-avatar-input" />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()} data-testid="profile-avatar-upload"><Upload className="w-3.5 h-3.5" /> Upload</Button>
              {avatarUrl && <Button variant="outline" size="sm" className="gap-1.5 text-rose-600" onClick={removeAvatar} data-testid="profile-avatar-remove"><Trash2 className="w-3.5 h-3.5" /> Remove</Button>}
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-2">
            <h2 className="text-base font-bold text-slate-900 mb-4">Details</h2>
            <form autoComplete="off" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={lbl}>First name</label><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1" autoComplete="off" data-testid="profile-first-name" /></div>
              <div><label className={lbl}>Surname</label><Input value={f.surname} onChange={(e) => set("surname", e.target.value)} className="mt-1" autoComplete="off" data-testid="profile-surname" /></div>
              <div><label className={lbl}>Job title</label><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} className="mt-1" autoComplete="off" data-testid="profile-jobtitle" /></div>
              <div><label className={lbl}>Phone</label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" autoComplete="off" data-testid="profile-phone" /></div>
              <div><label className={lbl}>Email</label><Input value={user?.email || ""} readOnly className="mt-1 bg-slate-50 text-slate-400" /></div>
            </form>
            <div className="mt-4"><label className={lbl}>Bio</label>
              <textarea value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={3} placeholder="A short bio about you"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="profile-bio" /></div>
            <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium text-slate-800">I clock in / out</div>
                <div className="text-[11px] text-slate-500">Turn on if you record attendance (e.g. engineers) — shows a running weekly total below.</div>
              </div>
              <Switch checked={f.requires_checkin} onCheckedChange={toggleCheckinReq} data-testid="profile-checkin-toggle" />
            </div>
          </div>
        </div>

        {/* Change password (same card, divided) */}
        <div className="border-t border-slate-100 mt-6 pt-5">
          <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-500" /> Change password</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><label className={lbl}>Current</label><Input type="password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} className="mt-1" data-testid="profile-pw-current" /></div>
            <div><label className={lbl}>New</label><Input type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} className={`mt-1${newCls}`} data-testid="profile-pw-new" /></div>
            <div><label className={lbl}>Confirm new</label><Input type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} className={`mt-1${confCls}`} data-testid="profile-pw-confirm" /></div>
          </div>
          <p className="mt-2 text-[11px] text-slate-500" data-testid="profile-pw-rule">{PASSWORD_RULE}</p>
        </div>

        {/* Shared footer actions */}
        <div className="border-t border-slate-100 mt-6 pt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" disabled={savingPw || !pw.current_password || !pw.new_password} onClick={changePassword} data-testid="profile-pw-save">
            {savingPw && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Update password
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={savingProfile} onClick={saveProfile} data-testid="profile-save">
            {savingProfile && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save changes
          </Button>
        </div>
      </div>

      {/* Timesheet (only for clock-in users) */}
      {f.requires_checkin && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mb-4" data-testid="profile-timesheet">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-500" /> Time &amp; attendance</h2>
            {ts?.open
              ? <Button className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white" disabled={ciBusy} onClick={() => doCheck("checkout")} data-testid="profile-checkout"><LogOut className="w-4 h-4" /> Check out</Button>
              : <Button className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={ciBusy} onClick={() => doCheck("checkin")} data-testid="profile-checkin"><LogIn className="w-4 h-4" /> Check in</Button>}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs text-slate-500">This week</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="profile-week-total">{(ts?.total_hours ?? 0).toFixed(1)}h</div>
              {f.weekly_hours ? <div className="text-[11px] text-slate-400">of {Number(f.weekly_hours)}h target</div> : null}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Status</div>
              <div className={`text-sm font-semibold mt-1 ${ts?.open ? "text-emerald-600" : "text-slate-500"}`}>{ts?.open ? "Checked in" : "Checked out"}</div>
              {ts?.open && <div className="text-[11px] text-slate-400">since {fmtTime(ts.open_since)}</div>}
            </div>
          </div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">This week's sessions</div>
          {(ts?.sessions || []).length === 0 ? <div className="text-sm text-slate-400 py-2">No check-ins yet this week.</div> : (
            <div className="space-y-1.5">
              {ts.sessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm rounded-lg border border-slate-100 px-3 py-2">
                  <span className="text-slate-600">{fmtTime(s.start)} → {s.end ? fmtTime(s.end) : <span className="text-emerald-600">in progress</span>}</span>
                  <span className="font-medium text-slate-800">{s.hours.toFixed(2)}h</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Personal / Calendar / Announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="profile-personal-card">
          <h2 className="text-base font-bold text-slate-900 mb-1">Personal</h2>
          <p className="text-sm text-slate-500 mb-3">Private to you — only you can see these.</p>
          <div className="space-y-2">
            <Link to="/me/notes" data-testid="profile-link-notes" className="group flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-amber-300 hover:bg-amber-50/40 transition-colors">
              <span className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><StickyNote className="w-4 h-4" /></span>
              <div className="min-w-0 flex-1"><div className="font-semibold text-slate-900 text-sm">My Notes</div><div className="text-xs text-slate-500">Jot down anything</div></div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500" />
            </Link>
            <Link to="/me/tasks" data-testid="profile-link-tasks" className="group flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors">
              <span className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><ListTodo className="w-4 h-4" /></span>
              <div className="min-w-0 flex-1"><div className="font-semibold text-slate-900 text-sm">My Tasks</div><div className="text-xs text-slate-500">A personal to-do list</div></div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500" />
            </Link>
          </div>
        </div>
        <CalendarCard />
        <MyPolicies />
        <MyHrDocuments />
        <AnnouncementsCard />
      </div>
      <MyExpenses refreshKey={expKey} />
      <MyContacts />
    </div>
  );
}

function CalendarCard() {
  const [st, setSt] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => { api.get("/calendar/status").then((r) => setSt(r.data)).catch(() => setSt(null)); }, []);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("calendar");
    if (p) { toast.success(`${p === "google" ? "Google" : "Outlook"} calendar connected`); window.history.replaceState({}, "", "/profile"); load(); }
  }, [load]);
  const connect = async (provider) => {
    try { const r = await api.get("/oauth/calendar/connect", { params: { provider } }); window.location.assign(r.data.authorization_url); }
    catch (e) { toast.error(e.response?.data?.detail || "Not configured yet"); }
  };
  const disconnect = async (provider) => { try { await api.post("/calendar/disconnect", null, { params: { provider } }); toast.success("Disconnected"); load(); } catch { toast.error("Failed"); } };
  const sync = async () => {
    setBusy(true);
    try { const r = await api.post("/calendar/sync"); toast.success(`Synced — ${r.data.pushed} pushed, ${r.data.pulled} updated`); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };
  const row = (provider, label, colour) => {
    const conf = st?.configured?.[provider]; const conn = st?.connected?.[provider]; const email = st?.email?.[provider];
    return (
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`cal-row-${provider}`}>
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${colour}`}><CalendarDays className="w-4 h-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-800 text-sm">{label}</div>
          <div className="text-[11px] text-slate-500 truncate">{!conf ? "Not configured yet" : conn ? (email || "Connected") : "Not connected"}</div>
        </div>
        {conf && (conn
          ? <Button size="sm" variant="outline" className="text-rose-600" onClick={() => disconnect(provider)} data-testid={`cal-disconnect-${provider}`}>Disconnect</Button>
          : <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => connect(provider)} data-testid={`cal-connect-${provider}`}>Connect</Button>)}
      </div>
    );
  };
  const anyConn = st?.connected?.google || st?.connected?.microsoft;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="profile-calendar-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-bold text-slate-900">Calendar sync</h2>
        {anyConn && <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={sync} data-testid="cal-sync"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Sync now</Button>}
      </div>
      <p className="text-sm text-slate-500 mb-3">Two-way sync your <b>My Tasks</b> with your calendar.</p>
      <div className="space-y-2">
        {row("google", "Google Calendar", "bg-red-50 text-red-600")}
        {row("microsoft", "Outlook Calendar", "bg-blue-50 text-blue-600")}
      </div>
    </div>
  );
}
